import { useId, useState } from "react";

const COMPANIES = [
  { label: "TCS", src: "/brand/tcs.png", height: 70, sizeBoost: 68 },
  { label: "EY", src: "/brand/EY.png", height: 48 },
  { label: "Samsung", src: "/brand/samsung.png", height: 52 },
  { label: "Target", src: "/brand/target.png", height: 46 },
  { label: "Sony", src: "/brand/sony.png", height: 42 },
  { label: "LTIMindtree", src: "/brand/ltimindtree.png", height: 58 },
  { label: "SLK", src: "/brand/SLK_Software_Logo.png", height: 58 },
   { label: "apple", src: "/brand/apple.png", height: 42 },
  { label: "micron", src: "/brand/micron.png", height: 58 },
  { label: "freshworks", src: "/brand/freshworks.png", height: 58 },
  { label: "Infosys BPM", src: "/brand/infosys.png", height: 58, sizeBoost: 66 },
  { label: "Moody's", src: "/brand/Moody's_new_logo.png", height: 40 },
  { label: "Splunk", src: "/brand/Splunk_logo.png", height: 46 },
  { label: "Fujitsu", src: "/brand/fujitsu-logo.png", height: 86, sizeBoost: 66 },
  { label: "Alight", src: "/brand/Alight_Solutions_Logo.png", height: 52 },
  { label: "Tech Mahindra", src: "/brand/Tech_Mahindra-Logo.wine.png", height: 40 },
  { label: "Genpact", src: "/brand/Genpact-Logo.wine.png", height: 104 },
  { label: "Waymo", src: "/brand/Waymo_logo.png", height: 58 },
  { label: "General Mills", src: "/brand/General_Mills_logo.png", height: 58 },
  { label: "Alstom", src: "/brand/Alstom_logo.png", height: 42 },
  { label: "BNSF Railway", src: "/brand/BNSF_Railway_Company_logo.png", height: 40 },
];

export default function TrustedLogos({
  variant = "marquee",
  surface = "light",
  eyebrow = "Trusted by top companies",
  className = "",
  // Lets a page swap in its own roster (e.g. a corporate-training track
  // page showing only the companies relevant to that track) without
  // touching every other page still using the site-wide default list.
  companies = COMPANIES,
}) {
  const id = useId();

  const isDark = surface === "dark";

  const eyebrowColor = isDark
    ? "text-[var(--cyan)]"
    : "text-royal";

  const lineColor = isDark
    ? "border-white/10"
    : "border-line";

  const cardSurface = isDark
    ? "bg-white/[0.03]"
    : "bg-gradient-to-b from-bg-alt to-white";

  return (
    <section
      data-testid="trusted-logos"
      className={`relative ${className}`}
      aria-labelledby={id}
    >
      {eyebrow ? (
        <div className="flex items-center justify-center gap-4">
          <span
            className="hidden sm:block h-px flex-1 max-w-20"
            style={{ backgroundColor: isDark ? "rgba(255,255,255,0.15)" : "var(--line)" }}
            aria-hidden="true"
          />
          <p
            id={id}
            className={`shrink-0 text-center font-mono-ui text-[11px] uppercase tracking-[0.24em] ${eyebrowColor}`}
          >
            {eyebrow}
          </p>
          <span
            className="hidden sm:block h-px flex-1 max-w-20"
            style={{ backgroundColor: isDark ? "rgba(255,255,255,0.15)" : "var(--line)" }}
            aria-hidden="true"
          />
        </div>
      ) : (
        <p id={id} className="sr-only">
          {eyebrow}
        </p>
      )}

      {variant === "marquee" ? (
        <div
          className={`mt-6 overflow-hidden border-b ${lineColor} ${cardSurface} shadow-[0_18px_40px_-30px_rgba(15,42,115,0.35)]`}
        >
          <div
            className="overflow-hidden py-8"
            style={{
              WebkitMaskImage:
                "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)",
              maskImage:
                "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)",
            }}
          >
            <div className="zt-marquee flex w-max items-center gap-4 lg:gap-6">
              {[...companies, ...companies].map((company, index) => (
                <Mark
                  key={`${company.label}-${index}`}
                  company={company}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`mt-6 overflow-hidden rounded-2xl border ${lineColor} ${cardSurface} shadow-[0_18px_40px_-30px_rgba(15,42,115,0.35)]`}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-y-6 gap-x-4 px-6 py-10">
            {companies.map((company) => (
              <div
                key={company.label}
                className="flex justify-center items-center"
              >
                <Mark company={company} />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

const BOX_HEIGHT = 78;
const CARD_CLASSES =
  "rounded-2xl border border-line bg-white shadow-[0_2px_6px_-2px_rgba(15,42,115,0.12)] card-hover-lift flex-shrink-0 flex items-center justify-center";

function Mark({ company }) {
  const [error, setError] = useState(false);
  // Most logos are roughly square or icon+wordmark, so the default 190px
  // box gives them plenty of room to reach their height cap. A handful are
  // a single flat wordmark (very wide relative to how tall the lettering
  // actually is) -- at the default box width those get squeezed down to a
  // sliver long before they'd reach their height cap, looking tiny next to
  // the squarer logos beside them. `company.width` lets one of those opt
  // into a wider box so it can actually reach a comparable height.
  const boxWidth = company.width || 190;
  // Clamp to the card's inner height so tall wordmarks (Fujitsu, Tech
  // Mahindra, Genpact) don't poke out past the rounded border. A few logo
  // files (TCS, Fujitsu, Infosys, Tech Mahindra) bake in a lot of empty
  // margin around the mark itself, so even at the same pixel height they
  // read visibly smaller than their neighbors -- `company.sizeBoost` sets
  // a per-logo cap for just those so the mark itself looks comparable,
  // without touching the box dimensions.
  const imgMaxHeight = Math.min(company.height, company.sizeBoost || 46);

  if (company.src && !error) {
    return (
      <div style={{ width: `${boxWidth}px`, height: `${BOX_HEIGHT}px` }} className={CARD_CLASSES}>
        <img
          src={company.src}
          alt={company.label}
          loading="lazy"
          onError={() => setError(true)}
          style={{
            maxHeight: `${imgMaxHeight}px`,
            maxWidth: `${boxWidth - 40}px`,
            width: "auto",
            objectFit: "contain",
          }}
          className="transition-transform duration-300 ease-in-out hover:scale-110"
        />
      </div>
    );
  }

  return (
    <div style={{ width: `${boxWidth}px`, height: `${BOX_HEIGHT}px` }} className={CARD_CLASSES}>
      <span className="text-lg font-semibold text-gray-700">
        {company.label}
      </span>
    </div>
  );
}